import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTickingNow } from '../../hooks/useTickingNow';
import { ReportAbsenceModal } from './ReportAbsenceModal';

interface ReportAbsenceButtonProps {
  sessionId: string;
  patientName: string;
  /** Email del paciente — no siempre disponible (ej. la fila de Vista diaria no lo trae). */
  patientEmail?: string;
  /** Scheduled start/end of this specific session, in the psychologist's own
   * timezone (epoch ms) — never the patient's, even if they differ. */
  startMs: number;
  endMs: number;
  className?: string;
  /** ID del proceso terapéutico — habilita el link al formulario de no agendamiento dentro del modal. */
  processId?: string;
}

/**
 * Self-contained "Reportar ausencia" trigger: visible only during the exact
 * window of the session (from its scheduled start to its scheduled end), and
 * opens the fixed institutional-policy modal on click. Mirrors the
 * self-managed pattern of JoinSessionButton.
 */
export const ReportAbsenceButton: React.FC<ReportAbsenceButtonProps> = ({
  sessionId,
  patientName,
  patientEmail,
  startMs,
  endMs,
  className,
  processId,
}) => {
  const now = useTickingNow(5000);
  const [modalOpen, setModalOpen] = useState(false);

  const isWithinWindow = now >= startMs && now < endMs;
  if (!isWithinWindow) return null;

  return (
    <>
      <div className={className}>
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid={`report-absence-btn-${sessionId}`}
              className="w-full rounded-full border-[#6D57BC] text-[#6D57BC] hover:bg-[#f3efff] hover:text-[#6D57BC]"
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(true);
              }}
            >
              Reportar ausencia
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[280px] text-center">
            Reportá la ausencia cuando el paciente no se presentó ni avisó. Te guía paso a paso: avisarle al paciente, esperar la tolerancia institucional y completar el formulario de no agendamiento si finalmente no se presenta.
          </TooltipContent>
        </Tooltip>
      </div>
      <ReportAbsenceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        sessionId={sessionId}
        sessionStartMs={startMs}
        patientName={patientName}
        patientEmail={patientEmail}
        processId={processId}
      />
    </>
  );
};

export default ReportAbsenceButton;
